import type { FieldsetProps } from "@mantine/core";
import {
  Box,
  Collapse,
  Fieldset,
  Group,
  Text,
  Title,
  UnstyledButton
} from "@mantine/core";
import { useDisclosure, useId } from "@mantine/hooks";
import {
  IconAlertTriangle,
  IconCaretDown,
  IconCaretRight
} from "@tabler/icons-react";
import { useEffect } from "react";
import { useMissingData } from "../../../../hooks/useMissingData";
import { THEME_VARS } from "../../../../utils/constants";
import { camelize } from "../../../../utils/SnakeCamelConversion";
import { IconHelpText } from "../../../IconHelpText/IconHelpText";
import { OrangeCircle } from "../../../OrangeCircle/OrangeCircle";

function useSafeMissingData() {
  try {
    return useMissingData();
  } catch {
    return {
      missingData: null,
      missingFields: null,
      exclusiveOrFields: null
    }; // Fallback in case useMissingData fails, e.g., in storybook-tests
  }
}

export interface FormSectionProps extends FieldsetProps {
  children: React.ReactNode;
  label: string;
  helpText?: string;
  isCollapsible?: boolean;
  isSubSection?: boolean;
  fieldNamesForMissingFields?: string[];
  defaultOpen?: boolean;
  forcedOpenState?: "open" | "closed";
  preview?: React.ReactNode;
  hasError?: boolean;
  hasMissingData?: boolean;
}

function FormSection({
  children,
  label,
  helpText,
  isCollapsible,
  isSubSection,
  defaultOpen = false,
  forcedOpenState,
  preview,
  fieldNamesForMissingFields = [],
  hasError,
  hasMissingData,
  ...other
}: FormSectionProps) {
  const [isCollapseOpen, { toggle: toggleCollapse, set: setCollapse }] =
    useDisclosure(defaultOpen);

  useEffect(() => {
    if (forcedOpenState !== undefined) {
      setCollapse(forcedOpenState === "open");
    }
  }, [forcedOpenState, setCollapse]);

  const collapseId = useId();

  const { missingData } = useSafeMissingData();
  const missingFields = Object.values(missingData?.missingFields ?? {}).flat();
  const isHighlightedFromMissingData =
    fieldNamesForMissingFields.length > 0 &&
    missingFields.some((field) =>
      fieldNamesForMissingFields.some((name) => {
        const camelCaseName = camelize(name);
        return camelCaseName.includes(field);
      })
    );
  const isHighlighted = hasMissingData || isHighlightedFromMissingData;

  const helpIcon = helpText && (
    <Box
      component="span"
      display="inline-flex"
      ml="calc(4px - var(--mantine-spacing-xs))"
      my="calc((1lh - 24px) / 2)"
      style={{ alignSelf: "flex-start" }}
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.preventDefault()}
    >
      <IconHelpText helpText={helpText} popoverPosition="top" />
    </Box>
  );

  return (
    <Fieldset bd="none" mb="sm" mt="sm" p={0} {...other}>
      <Group mb="xs">
        {isCollapsible ? (
          <UnstyledButton
            aria-controls={collapseId}
            aria-expanded={isCollapseOpen}
            c="inherit"
            flex={1}
            p={0}
            variant="transparent"
            onClick={toggleCollapse}
          >
            <Group align="flex-start" gap="xs">
              {isCollapseOpen ? (
                <IconCaretDown strokeWidth="1" />
              ) : (
                <IconCaretRight strokeWidth="1" />
              )}
              <Box>
                <Title
                  fw={isSubSection ? 400 : undefined}
                  mb={0}
                  mt={3}
                  order={3}
                  size="h6"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--mantine-spacing-xs)"
                  }}
                >
                  {isHighlighted && <OrangeCircle />} {label}
                  {hasError && (
                    <IconAlertTriangle
                      aria-label="Fehler"
                      color={THEME_VARS.error[6]}
                      role="img"
                      size="1.1rem"
                    />
                  )}
                  {helpIcon}
                </Title>
                {preview && !isCollapseOpen && isCollapsible && (
                  <Text span>{preview}</Text>
                )}
              </Box>
            </Group>
          </UnstyledButton>
        ) : (
          <Title
            fw={isSubSection ? 400 : undefined}
            order={3}
            size="h6"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--mantine-spacing-xs)"
            }}
          >
            {isHighlighted && <OrangeCircle />} {label}
            {helpIcon}
          </Title>
        )}
      </Group>
      {isCollapsible ? (
        <Collapse expanded={isCollapseOpen} id={collapseId}>
          {children}
        </Collapse>
      ) : (
        <>{children}</>
      )}
    </Fieldset>
  );
}

export { FormSection };
